// JOINING A LEAD ROW TO THE PORTAL'S PRODUCT-USAGE DATA.
//
// The usage columns on the leads table (last login, last software access,
// subscription, analysis runs, …) come from GET /api/hubspot/users — one call
// for the whole screen, whose `by_company` map keys the per-company aggregates
// by HubSpot company id. This module is the pure half of those columns: the
// row→aggregate join, the sort keys, and the small format decisions, with no
// React and no network, so `node scripts/test-leadUsage.mjs` can pin every
// boundary exactly as the table runs it.
//
// THE JOIN REUSES THE MATCH THE APP ALREADY MADE. Every directory row has been
// resolved against the mirror's match index by the data layer (annotateRow in
// lib/data.jsx, run by useLeadsCache), which records the verdict on
// row._hubspot: matched / ambiguous / companyId. That matcher is exact-key,
// refuses coin flips, and is tested; re-matching here would be a second
// matcher that could disagree with the first, so this module only ever READS
// the recorded verdict. An ambiguous or unmatched row joins to nothing —
// "we are not sure which company this is" must never borrow another
// company's usage numbers.
//
// UNREAD ≠ EMPTY, EVERYWHERE. A missing value is '' / null here, never 0:
// zero would sort a company with no data as "logged in in 1970" and render as
// a fact nobody holds. The table turns every null into an em dash.
import { hubspotDate } from './hubspot.js'

/**
 * The HubSpot company id this row was confidently matched to, or ''.
 * Reads the data layer's recorded verdict (see module comment) — never
 * re-matches, and treats an ambiguous hit as no match, same as everywhere
 * else in the app.
 */
export function usageCompanyIdOf(row) {
  const hs = row && row._hubspot
  if (!hs || !hs.matched || hs.ambiguous) return ''
  return String(hs.companyId || '')
}

/**
 * The per-company usage aggregate for one lead row, or null.
 * Null means one of three different silences — usage payload absent or
 * unavailable, row unmatched, or matched but HubSpot holds no usage for the
 * company — and the caller's dash tooltip is where the three are told apart
 * (usageDashReason below).
 */
export function usageForLead(row, usage) {
  if (!usage || usage.available === false) return null
  const id = usageCompanyIdOf(row)
  if (!id) return null
  const byCompany = usage.by_company
  const agg = byCompany && typeof byCompany === 'object' ? byCompany[id] : null
  return agg && typeof agg === 'object' ? agg : null
}

/**
 * Sort key for a mirror datetime: epoch ms, or '' when there is no parseable
 * value. '' (not 0) because DataTable sorts empties last — 0 would file every
 * company with no data as January 1970 and mix it in among real dates.
 */
export function usageEpoch(value) {
  const d = hubspotDate(value)
  return d ? d.getTime() : ''
}

/**
 * A mirror count (project_count, blueprint_count) as a number, or '' when the
 * property is absent or unparseable. The guard exists because Number('') is 0,
 * and a company with no project count on file must not read as "0 projects".
 */
export function usageNumber(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const n = Number(raw)
  return Number.isFinite(n) ? n : ''
}

/**
 * How many product users a company aggregate covers, or null when unknown.
 * The lambda's by_company `users` field is a count; this also accepts a list
 * (length) so a payload-shape drift degrades to the same honest number rather
 * than NaN. A real 0 stays 0 — that is a held fact, unlike an absent field.
 */
export function usersCountOf(agg) {
  const v = agg ? agg.users : null
  if (Array.isArray(v)) return v.length
  const raw = String(v ?? '').trim()
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/**
 * A mirror boolean (payment_method_attached) as true/false, or null when the
 * property is absent. Absent is not "no": a contact the sync has not
 * re-hydrated yet simply has nothing to say about payment methods.
 */
export function usageBool(value) {
  if (value === true) return true
  if (value === false) return false
  const raw = String(value ?? '').trim().toLowerCase()
  if (!raw) return null
  if (raw === 'true' || raw === 'yes' || raw === '1') return true
  if (raw === 'false' || raw === 'no' || raw === '0') return false
  return null
}

// The subscription pill turns amber this many days before the end date — early
// enough for a renewal conversation to happen before the lockout, not after.
export const SUBSCRIPTION_WARN_DAYS = 14

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Where a subscription stands relative to `now`:
 *   { state: 'expired', days }  — ended `days` full days ago (0 = today)
 *   { state: 'ending',  days }  — ends within SUBSCRIPTION_WARN_DAYS (0 = today)
 *   { state: 'active',  days }  — ends later than the warning window
 *   null                        — no parseable end date: nothing can be claimed
 * `days` is whole 24h periods, floor for the past and ceil for the future, so
 * "Expired 1d ago" and "Ends in 1d" both mean what a rep would mean by them.
 */
export function subscriptionStatus(value, now = Date.now()) {
  const d = hubspotDate(value)
  if (!d) return null
  const delta = d.getTime() - now
  if (delta < 0) return { state: 'expired', days: Math.floor(-delta / DAY_MS), at: d }
  const days = Math.ceil(delta / DAY_MS)
  // The window is compared on the raw delta, not the rounded day count — an
  // end 13.5 days out is inside a "<14 days" window even though its display
  // count rounds up to 14.
  if (delta < SUBSCRIPTION_WARN_DAYS * DAY_MS) return { state: 'ending', days, at: d }
  return { state: 'active', days, at: d }
}

/**
 * The server's own rule sentence for one by_company field, else `fallback`.
 * route_users ships definitions.by_company_fields built from the SAME tuples
 * its aggregate builder iterates, so quoting it verbatim keeps the column
 * tooltip and the aggregation code from drifting apart. The fallback (the
 * same sentence, hardcoded at the call site) only covers a payload that has
 * not landed yet or predates the field definitions.
 */
export function usageFieldRule(usage, field, fallback = '') {
  const defs = usage && usage.definitions ? usage.definitions.by_company_fields : null
  const rule = defs && typeof defs === 'object' ? defs[field] : ''
  return typeof rule === 'string' && rule.trim() ? rule : fallback
}

/**
 * Why a usage cell shows a dash, in words for its tooltip. Three different
 * silences all render as the same dash, and the title is where the row owes
 * the reader which one this is — "we could not read" is not "there is
 * nothing", and "no CRM match" is not "no usage".
 */
export function usageDashReason(row, usage) {
  if (!usage) return 'The usage data is still loading.'
  if (usage.available === false) {
    return 'The usage data could not be read, so nothing can be said here — not the same as no usage.'
  }
  if (!usageCompanyIdOf(row)) {
    return 'This lead has no confidently matched HubSpot record, so there is no usage record to read.'
  }
  return 'HubSpot holds no value for this field on this company.'
}

/**
 * The one honest sentence the table's status area owes about the usage read,
 * or null when there is nothing to admit (loaded and complete, or still
 * loading — a read in flight is not yet a failure).
 * Returns { tone: 'warning'|'neutral', label, detail } for the badge.
 */
export function usageStatusNote(usage) {
  if (!usage) return null
  if (usage.available === false) {
    return {
      tone: 'warning',
      label: 'Usage data could not be read',
      detail:
        (usage.reason || 'The HubSpot usage endpoint did not answer.') +
        ' The usage columns show dashes because nothing was read — not because nobody uses the software.',
    }
  }
  if (usage.configured === false) {
    return {
      tone: 'neutral',
      label: 'Usage columns empty — HubSpot not connected',
      detail:
        'Nobody has connected HubSpot in this workspace, so there is no product-usage data to read.',
    }
  }
  if (usage.complete === false) {
    return {
      tone: 'warning',
      label: 'Usage data incomplete',
      detail:
        (usage.reason || 'The usage scan stopped before reading every contact.') +
        ' Some companies may show dashes that a complete read would fill.',
    }
  }
  return null
}

// ── last_demo__recording_date, the one hand-typed date ───────────────────────
// 2026-09-03: the owner dropped 14 of these columns (lead imported / status /
// source / source detail, offer type, employment status, LinkedIn, the four
// Zoom webinar engagement figures, the last-webinar string, and the two
// company-level recording props), and the properties behind them stopped
// being mirrored at the same time — so the enum-list, external-link and
// attendance-percentage helpers that lived here went with their only callers.
// What is left serves ONE surviving column, Last demo, and one COMPANY-level
// property: last_demo__recording_date. It keeps the module's law — absent is
// '' / null, never 0, never a 1970 date — and it must still behave when
// pointed at a payload that simply lacks the field, because that is exactly
// what it is pointed at for the few minutes a sync deploy is in flight.

// The only string shapes usageStringDay is willing to call a DATE. Everything
// else a hand can type — 'March 2026', '8/26/2026', 'sometime last spring' —
// may well survive new Date(), but parseable is not the same as naming a day:
// Date() quietly invents the 1st for 'March 2026', reads an ISO date-only
// string as UTC midnight (so it renders as the PREVIOUS day in every US
// timezone this app's leads live in), and reads a 10-digit Unix-seconds stamp
// as epoch milliseconds (January 1970 — the exact date this module's one law
// forbids). A confident wrong day is worse than the raw string, so anything
// outside these shapes renders raw.
const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * The exact-day reading of a hand-typed date string, or null when the string
 * does not unambiguously name one. Returns { ms, utc }: `ms` is the sort
 * instant, `utc: true` means the string named a calendar DAY with no clock
 * attached, so it must be rendered on the UTC clock too — rendering UTC
 * midnight in local time is how '2026-08-26' becomes "Aug 25" in Texas.
 */
function handTypedDay(raw) {
  const m = ISO_DAY_RE.exec(raw)
  if (m) {
    const y = Number(m[1])
    const mo = Number(m[2])
    const d = Number(m[3])
    const ms = Date.UTC(y, mo - 1, d)
    // Date.UTC rolls an impossible day over into a real one ('2026-13-40' →
    // some confident-looking February date). A string that doesn't round-trip
    // did not name the day it renders as, so it renders raw instead.
    const back = new Date(ms)
    if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
      return null
    }
    return { ms, utc: true }
  }
  if (/^\d+$/.test(raw)) {
    // Pure digits: only a full epoch-MILLISECONDS value (13+ digits, every
    // date after Sep 2001) is a date here. hubspotDate's epoch branch starts
    // at 10 digits — right for the mirror's machine-written datetimes, wrong
    // for a hand-typed property where 10 digits is a Unix-SECONDS stamp that
    // an ms read would file in January 1970.
    if (raw.length < 13) return null
    const parsed = hubspotDate(raw)
    return parsed ? { ms: parsed.getTime(), utc: false } : null
  }
  if (/^\d{4}-\d{2}-\d{2}[T ]/.test(raw)) {
    // A full ISO datetime carries its own clock (or offset), so the ordinary
    // local rendering is the honest one — same as the real datetime columns.
    const parsed = hubspotDate(raw)
    return parsed ? { ms: parsed.getTime(), utc: false } : null
  }
  return null
}

/**
 * A day label for a STRING-typed date property (last_demo__recording_date is
 * `string` in the portal, not `datetime` — someone types it by hand). When
 * the string unambiguously names a day (see handTypedDay: ISO date-only, ISO
 * datetime, or a 13+-digit epoch-ms stamp), render the same "Aug 26, 2026"
 * day format as the real datetime columns — an ISO date-only string on the
 * UTC clock, so the rendered day always equals the typed day. Anything else
 * held comes back RAW: the reader deciphering "sometime last spring" beats a
 * wrong-but-confident date, and beats a dash that hides a value HubSpot
 * actually holds. '' only when the property is truly empty.
 */
export function usageStringDay(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const day = handTypedDay(raw)
  if (!day) return raw
  const opts = { month: 'short', day: 'numeric', year: 'numeric' }
  return new Date(day.ms).toLocaleDateString(undefined, day.utc ? { ...opts, timeZone: 'UTC' } : opts)
}

/**
 * Sort key twin of usageStringDay: epoch ms for exactly the strings it
 * renders as dates, '' for everything else. The two must share handTypedDay —
 * a cell that RENDERS the raw string but SORTS as a 1970 epoch (or as the
 * previous day) would order the column by dates the reader cannot see. Raw-
 * rendered strings sort '' (among the absent rows, which DataTable files
 * last): there is no honest position for "sometime last spring" on a date
 * axis.
 */
export function usageStringDayEpoch(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const day = handTypedDay(raw)
  return day ? day.ms : ''
}
